import React, { useEffect, useRef, useState } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import './XAir.css';

/* ══════════════════════════════════════════════
   Iconos SVG internos
   ══════════════════════════════════════════════ */

const IconWind = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.7 7.7a2.5 2.5 0 111.8 4.3H2M9.6 4.6a2 2 0 113 2.4H2M12.6 19.4a2 2 0 10-3-2.4H2"/>
  </svg>
);

const IconHeatmap = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <circle cx="8" cy="8" r="2"/><circle cx="16" cy="9" r="2.5"/><circle cx="10" cy="15" r="2.2"/>
  </svg>
);

const IconAlert = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconCompare = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 12h4l3-8 4 16 3-8h4"/>
  </svg>
);

const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

/* Config estática de features — textos vienen del JSON */
const FEATURES = [
  { key: 'feature_1', iconColor: 'rgba(79,157,111,0.15)',  Icon: () => <IconWind     color="#4f9d6f" /> },
  { key: 'feature_2', iconColor: 'rgba(192,138,62,0.15)',  Icon: () => <IconAlert    color="#c08a3e" /> },
  { key: 'feature_3', iconColor: 'rgba(47,138,138,0.15)',  Icon: () => <IconHeatmap  color="#2f8a8a" /> },
  { key: 'feature_4', iconColor: 'rgba(94,125,153,0.15)',  Icon: () => <IconCompare  color="#5e7d99" /> },
];

/* ══════════════════════════════════════════════
   Datos demo de sensores (en producción vienen del JSON i18n
   o de la API directamente)
   ══════════════════════════════════════════════ */
const DEMO_SENSORS = [
  { id:'E1', name:'Estación 1', loc:'Entrada principal',  x:0.14, y:0.36, pm25:18.4,  pm10:42.1,  cls:'good'     },
  { id:'E2', name:'Estación 2', loc:'Cancha deportiva',   x:0.27, y:0.44, pm25:34.7,  pm10:78.3,  cls:'warn'     },
  { id:'E3', name:'Estación 3', loc:'Bloque A exterior',  x:0.38, y:0.52, pm25:62.1,  pm10:105.4, cls:'danger'   },
  { id:'E4', name:'Estación 4', loc:'Bloque C planta 1',  x:0.51, y:0.57, pm25:87.4,  pm10:134.2, cls:'danger'   },
  { id:'E5', name:'Estación 5', loc:'Bloque D nivel 2',   x:0.62, y:0.48, pm25:112.8, pm10:201.3, cls:'critical' },
  { id:'E6', name:'Estación 6', loc:'Zona estacionam.',   x:0.74, y:0.42, pm25:55.3,  pm10:92.7,  cls:'danger'   },
  { id:'E7', name:'Estación 7', loc:'Borde norte',        x:0.48, y:0.30, pm25:28.9,  pm10:61.5,  cls:'warn'     },
];

const CLS_COLORS = {
  good:     '#4f9d6f',
  warn:     '#c08a3e',
  danger:   '#bb5a4c',
  critical: '#8e2f6f',
};

/* ══════════════════════════════════════════════
   Interpolación de color del heatmap (continua)
   PM2.5 normalizada a 0-1 sobre 250 µg/m³
   ══════════════════════════════════════════════ */
function pmColor(pm25) {
  // Escala AQI-style: Bueno → Aceptable → Moderado → G.S* → Dañino → Peligro
  // Optimizada para fondo oscuro · gradiente vibrante verde→amarillo→rojo→magenta
  const stops = [
    [0.00,  74, 222, 128],   // verde · Bueno
    [0.12, 134, 239, 100],   // verde-amarillo
    [0.20, 251, 191,  36],   // amarillo · Aceptable
    [0.28, 251, 146,  60],   // naranja claro
    [0.36, 249, 115,  22],   // naranja · Moderado
    [0.45, 239,  68,  68],   // rojo · G.S*
    [0.58, 220,  38,  38],   // rojo intenso · Dañino
    [0.75, 192,  38, 211],   // magenta · Peligro
    [1.00, 162,  28, 175],   // morado oscuro
  ];
  const t = Math.min(pm25 / 250, 1);
  for (let i = 1; i < stops.length; i++) {
    if (t <= stops[i][0]) {
      const lo = stops[i-1], hi = stops[i];
      const f = (t - lo[0]) / (hi[0] - lo[0]);
      return [
        Math.round(lo[1] + f * (hi[1] - lo[1])),
        Math.round(lo[2] + f * (hi[2] - lo[2])),
        Math.round(lo[3] + f * (hi[3] - lo[3])),
      ];
    }
  }
  return [162, 28, 175];
}

/* ══════════════════════════════════════════════
   Renderizado del mapa base (light aerial style)
   ══════════════════════════════════════════════ */
function drawMap(canvas) {
  const W = canvas.width, H = canvas.height;
  const ctx = canvas.getContext('2d');

  // base: oscuro casi-negro
  ctx.fillStyle = '#0a0f18';
  ctx.fillRect(0, 0, W, H);

  // calles principales — líneas sutiles más claras
  ctx.strokeStyle = 'rgba(120, 130, 160, 0.18)';
  ctx.lineWidth = 22;
  ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(0, H*0.35); ctx.lineTo(W, H*0.28); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(0, H*0.68); ctx.lineTo(W, H*0.72); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(W*0.45, 0); ctx.lineTo(W*0.38, H); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(W*0.78, 0); ctx.lineTo(W*0.82, H); ctx.stroke();
  ctx.strokeStyle = 'rgba(120, 130, 160, 0.12)';
  ctx.lineWidth = 10;
  ctx.beginPath(); ctx.moveTo(0, H*0.5); ctx.lineTo(W*0.45, H*0.55); ctx.stroke();

  // líneas centrales discontinuas (más tenues)
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.lineWidth = 1;
  ctx.setLineDash([12, 10]);
  ctx.beginPath(); ctx.moveTo(0, H*0.35); ctx.lineTo(W, H*0.28); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(W*0.45, 0); ctx.lineTo(W*0.38, H); ctx.stroke();
  ctx.setLineDash([]);

  // edificios — siluetas grises tenues sobre fondo oscuro
  const buildings = [
    {x:0.05,y:0.40,w:0.12,h:0.18,c:'rgba(120,130,160,0.14)',rc:'rgba(140,150,180,0.18)'},
    {x:0.20,y:0.38,w:0.08,h:0.10,c:'rgba(120,130,160,0.12)',rc:'rgba(140,150,180,0.16)'},
    {x:0.25,y:0.50,w:0.06,h:0.08,c:'rgba(120,130,160,0.15)',rc:'rgba(140,150,180,0.20)'},
    {x:0.33,y:0.42,w:0.10,h:0.14,c:'rgba(120,130,160,0.13)',rc:'rgba(140,150,180,0.17)'},
    {x:0.37,y:0.58,w:0.08,h:0.08,c:'rgba(120,130,160,0.14)',rc:'rgba(140,150,180,0.18)'},
    {x:0.47,y:0.44,w:0.14,h:0.18,c:'rgba(120,130,160,0.16)',rc:'rgba(140,150,180,0.22)'},
    {x:0.60,y:0.40,w:0.10,h:0.12,c:'rgba(120,130,160,0.13)',rc:'rgba(140,150,180,0.17)'},
    {x:0.65,y:0.55,w:0.07,h:0.09,c:'rgba(120,130,160,0.14)',rc:'rgba(140,150,180,0.19)'},
    {x:0.10,y:0.55,w:0.09,h:0.12,c:'rgba(120,130,160,0.12)',rc:'rgba(140,150,180,0.16)'},
    {x:0.72,y:0.45,w:0.08,h:0.08,c:'rgba(120,130,160,0.13)',rc:'rgba(140,150,180,0.17)'},
  ];
  buildings.forEach(b => {
    const x = b.x*W, y = b.y*H, w = b.w*W, h = b.h*H;
    ctx.fillStyle = b.c; ctx.fillRect(x, y, w, h);
    ctx.fillStyle = b.rc; ctx.fillRect(x, y, w, h*0.25);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
    ctx.lineWidth = 1;
    ctx.strokeRect(x+0.5, y+0.5, w-1, h-1);
  });

  // áreas verdes — apagadas para no competir con el heatmap
  const greens = [
    {x:0.05,y:0.63,w:0.10,h:0.08},{x:0.15,y:0.66,w:0.07,h:0.06},
    {x:0.42,y:0.65,w:0.08,h:0.07},{x:0.50,y:0.68,w:0.06,h:0.06},
    {x:0.22,y:0.70,w:0.08,h:0.06},{x:0.68,y:0.60,w:0.05,h:0.05},
  ];
  greens.forEach(g => {
    const cx = (g.x + g.w/2) * W;
    const cy = (g.y + g.h/2) * H;
    const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, g.w*W);
    grd.addColorStop(0, 'rgba(34, 90, 50, 0.35)');
    grd.addColorStop(0.6, 'rgba(34, 90, 50, 0.18)');
    grd.addColorStop(1, 'rgba(34, 90, 50, 0)');
    ctx.fillStyle = grd;
    ctx.beginPath();
    ctx.ellipse(cx, cy, g.w*W*0.55, g.h*H*0.55, 0, 0, Math.PI*2);
    ctx.fill();
  });
}

/* ══════════════════════════════════════════════
   Heatmap: gradientes radiales por sensor
   ══════════════════════════════════════════════ */
function drawHeat(canvas, sensors) {
  const W = canvas.width, H = canvas.height;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, W, H);
  ctx.globalCompositeOperation = 'screen';

  sensors.forEach(s => {
    const cx = s.x * W, cy = s.y * H;
    const norm = s.pm25 / 250;
    const radius = W * (0.14 + norm * 0.18);
    const [r, g, b] = pmColor(s.pm25);
    const alpha = 0.65 + norm * 0.30;

    const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    grd.addColorStop(0.00, `rgba(${r},${g},${b},${alpha})`);
    grd.addColorStop(0.30, `rgba(${r},${g},${b},${(alpha*0.70).toFixed(2)})`);
    grd.addColorStop(0.60, `rgba(${r},${g},${b},${(alpha*0.35).toFixed(2)})`);
    grd.addColorStop(1.00, `rgba(${r},${g},${b},0)`);
    ctx.fillStyle = grd;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI*2);
    ctx.fill();
  });

  ctx.globalCompositeOperation = 'source-over';
}

/* ══════════════════════════════════════════════
   Componente principal
   ══════════════════════════════════════════════ */
const XAir = function XAir({ data, sensors }) {
  const [t] = useTranslation('common');
  const sectionRef = useRef(null);
  const mapWrapRef = useRef(null);
  const mapCanvasRef = useRef(null);
  const heatCanvasRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [selectedId, setSelectedId] = useState('E5');

  // Permitir override de sensores vía props (para API real); fallback a demo
  const sensorData = Array.isArray(sensors) && sensors.length > 0 ? sensors : DEMO_SENSORS;
  const selected = sensorData.find(s => s.id === selectedId) || sensorData[0];

  // ── Textos desde common.json → xAgro.air ──
  const eyebrow  = t(`${data}.eyebrow`);
  const title    = t(`${data}.title`);
  const subtitle = t(`${data}.subtitle`);
  const ctaText  = t(`${data}.cta`);

  // Header del panel
  const panelTitle    = t(`${data}.panel_title`,    { defaultValue: 'Zona Industrial · Guayaquil' });
  const panelLive     = t(`${data}.panel_live`,     { defaultValue: 'EN VIVO' });
  const panelTimestamp = t(`${data}.panel_timestamp`,{ defaultValue: 'Hace 2 min' });
  const legendTitle   = t(`${data}.legend_title`,   { defaultValue: 'PM 2.5 (µg/m³)' });
  const sensorsLabel  = t(`${data}.sensors_label`,  { defaultValue: 'Red de sensores' });

  // IntersectionObserver para animación de entrada
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Dibujar mapa + heatmap (responsive con ResizeObserver)
  useEffect(() => {
    const wrap = mapWrapRef.current;
    const mapCanvas = mapCanvasRef.current;
    const heatCanvas = heatCanvasRef.current;
    if (!wrap || !mapCanvas || !heatCanvas) return;

    const render = () => {
      const W = wrap.clientWidth;
      const H = wrap.clientHeight;
      if (W === 0 || H === 0) return;
      [mapCanvas, heatCanvas].forEach(c => { c.width = W; c.height = H; });
      drawMap(mapCanvas);
      drawHeat(heatCanvas, sensorData);
    };

    render();
    const ro = new ResizeObserver(render);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [sensorData]);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    else window.location.hash = '#contact';
  };

  return (
    <section
      id="xAgroAir"
      className="xAir-section"
      ref={sectionRef}
      data-visible={visible}
    >
      <div className="xagro-bg-pattern" aria-hidden="true" />

      <div className="xAir-wrapper">

        {/* ── Encabezado ── */}
        <div className="xAir-header">
          <div className="xagro-badge xagro-badge--dark">
            <span className="xagro-badge-dot xagro-badge-dot--animated" />
            {eyebrow}
          </div>
          <h2 className="xAir-title">{title}</h2>
          <p className="xAir-subtitle">{subtitle}</p>
        </div>

        <div className="xAir-layout">

          {/* ── Columna izquierda: features ── */}
          <div className="xAir-features">
            {FEATURES.map(({ key, iconColor, Icon }) => (
              <div key={key} className="xAir-feature">
                <div className="xAir-feature-icon" style={{ background: iconColor }}>
                  <Icon />
                </div>
                <div className="xAir-feature-body">
                  <span className="xAir-feature-title">{t(`${data}.${key}_title`)}</span>
                  <span className="xAir-feature-desc">{t(`${data}.${key}_desc`)}</span>
                </div>
              </div>
            ))}

            <button className="xAir-cta" onClick={scrollToContact}>
              {ctaText}
              <IconArrow />
            </button>
          </div>

          {/* ── Columna derecha: heatmap ── */}
          <div className="xAir-panel" role="region" aria-label={panelTitle}>

            {/* Header del panel */}
            <div className="xAir-panel-header">
              <div className="xAir-panel-title-block">
                <span className="xAir-panel-title">{panelTitle}</span>
                <span className="xAir-panel-meta">{panelTimestamp}</span>
              </div>
              <span className="xAir-live-badge">
                <span className="xAir-live-dot" />
                {panelLive}
              </span>
            </div>

            {/* Mapa con heatmap */}
            <div className="xAir-map-wrap" ref={mapWrapRef}>
              <canvas ref={mapCanvasRef} className="xAir-canvas" />
              <canvas ref={heatCanvasRef} className="xAir-canvas xAir-canvas--heat" />

              {/* Pines de sensores */}
              <div className="xAir-pins-layer">
                                {sensorData.map(s => {
                  const [pr, pg, pb] = pmColor(s.pm25);
                  return (
                  <button
                    key={s.id}
                    className={`xAir-pin xAir-pin--${s.cls} ${s.id === selectedId ? 'is-selected' : ''}`}
                    style={{
                      left: `${s.x*100}%`,
                      top: `${s.y*100}%`,
                      '--pin-color': `rgb(${pr},${pg},${pb})`,
                    }}
                    onClick={() => setSelectedId(s.id)}
                    aria-label={`${s.name}: PM2.5 ${s.pm25} µg/m³`}
                  >
                    <span className="xAir-pin-label">
                      {s.id} · {s.pm25} µg/m³
                    </span>
                  </button>
                  );
                })}
              </div>

              {/* Leyenda */}
              <div className="xAir-legend">
                <div className="xAir-legend-title">{legendTitle}</div>
                <div className="xAir-legend-bar" />
                <div className="xAir-legend-labels">
                  <span>0</span><span>30</span><span>60</span><span>100</span><span>150</span><span>250+</span>
                </div>
              </div>
            </div>

            {/* Footer del panel: estación seleccionada + lista compacta */}
            <div className="xAir-panel-footer">
              <div className="xAir-selected">
                <div className="xAir-selected-info">
                  <span className="xAir-selected-name">{selected.name}</span>
                  <span className="xAir-selected-loc">{selected.loc}</span>
                </div>
                <div className="xAir-selected-readings">
                  <div className="xAir-reading">
                    <span className="xAir-reading-val" style={{ color: CLS_COLORS[selected.cls] }}>
                      {selected.pm25}
                    </span>
                    <span className="xAir-reading-lbl">PM 2.5</span>
                  </div>
                  <div className="xAir-reading">
                    <span className="xAir-reading-val">{selected.pm10}</span>
                    <span className="xAir-reading-lbl">PM 10</span>
                  </div>
                </div>
              </div>
              <div className="xAir-sensor-strip" role="list">
                {sensorData.map(s => (
                  <button
                    key={s.id}
                    role="listitem"
                    className={`xAir-strip-item ${s.id === selectedId ? 'is-selected' : ''}`}
                    onClick={() => setSelectedId(s.id)}
                    aria-label={`Seleccionar ${s.name}`}
                  >
                    <span className="xAir-strip-dot" style={{ background: CLS_COLORS[s.cls] }} />
                    <span className="xAir-strip-id">{s.id}</span>
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default withTranslation('common')(XAir);